import { useAuth } from "@clerk/clerk-react";
import { Box, Grid } from "@mui/material";
import { APIProvider } from "@vis.gl/react-google-maps";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { BrandsContext } from "contexts/Brands";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  BentoBrand,
  MetadataType,
  SearchParams,
  Sort,
} from "schemas/dashboard";

import { API_KEY } from "components/LocationAutocomplete/useAutoCompletePrediction";
import PopoverSavedCollection from "components/PopoverSavedCollections";
import { PAGE_VISITED } from "constants/trackingProps";
import { arrayQuery, fetcherAuth } from "utils/api";
import {
  getCategoryIdParams,
  getMinAndMaxInstagram,
} from "utils/discoverSearch";
import {
  DISCOVER_SEARCH_PARAMS,
  DISPLAYED_CITY_DIALOG,
} from "utils/localStorage";
import { trackEvent } from "utils/tracking";
import { usePageSize } from "utils/usePageSize";

import BrandsList from "./BrandsList";
import NoFilterResultBrands from "./BrandsList/NoFilterResultBrands";
import SimilarBrands from "./BrandsList/SimilarBrands";
import CategoryFilter from "./CategoryFilter";
import CityDialog from "./Dialogs/CityDialog";
import DiscoverFilter from "./DiscoverFilter";
import OpenSendDialog from "./OpenSendDialog";
import styles from "./styles";

const RESULTS_PER_PAGE = 20;

export default function DiscoverResults() {
  const { breaksDiscoverScreen } = usePageSize();
  const { setProfileDialogOpen } = useContext(OnboardingDialogsContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { upgradeDialogSource } = useContext(SubscriptionContext);
  const {
    currentOrg,
    profile,
    hideCityDialog,
    setHideCityDialog,
    searchFilters,
    setSearchFilters,
  } = useContext(OrganizationUserContext);
  const { profileDialogOpen, incompleteProfile } = useContext(
    OnboardingDialogsContext,
  );

  const [profileDialogWasOpened, setProfileDialogWasOpened] = useState(false);

  const [ref, isBrandsRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const [similarBrandsRef, isSimilarBrandsRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const [noFilterResultsRef, isNoFilterResultsRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  const headRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const brandQuery = searchParams.get(SearchParams.QUERY_DISCOVER);
  const {
    topLevelCategories,
    categoriesById,
    categoriesLoading,
    fetchBentoCategories,
  } = useContext(BentoCategoriesContext);
  const {
    selectedMetadataTags,
    brands,
    setBrands,
    setNoFilterResultBrands,
    abortController,
    setSimilarBrands,
    similarBrandsCursor,
    setSimilarBrandsCursor,
    discoverFilterParams,
    cursor,
    setCursor,
    noFilterResultsCursor,
    setNoFilterResultsCursor,
  } = useContext(BrandsContext);

  // State variables
  const [brandsLoading, setBrandsLoading] = useState<boolean>(true);
  const [fetchMoreLoading, setFetchMoreLoading] = useState<boolean>(false);
  const [noFilterResultsLoading, setNoFilterResultsLoading] =
    useState<boolean>(false);
  const [noFilterResultsFetchMoreLoading, setNoFilterResultsFetchMoreLoading] =
    useState<boolean>(false);
  const [noFilterResultsLimit, setNoFilterResultsLimit] =
    useState<boolean>(false);
  const [hitsLimit, setHitsLimit] = useState<boolean>(false);
  const [similarBrandsLimit, setSimilarBrandsLimit] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const [openCityDialog, setOpenCityDialog] = useState(false);

  const resultsRef = useRef<HTMLDivElement>(null);

  const [lastSearchQuery, setLastSearchQuery] = useState<string | null>(null);

  const {
    selectedSort,
    selectedCategoryIds,
    selectedCompanyTypes,
    selectedContactTypes,
    selectedInstagramFollowings,
    selectedTargetGenders,
    selectedLocations,
    selectedGeopoints,
    selectedCompensationTypes,
    selectedLanguages,
  } = { ...discoverFilterParams };

  const query = sessionStorage.getItem(DISCOVER_SEARCH_PARAMS);

  const fetchInspiredBrands = async (
    bentoBrands: BentoBrand[],
    avoidBrands: BentoBrand[],
    cursor: number,
  ) => {
    if (!currentOrg?.id) {
      setFetchMoreLoading(false);
      return;
    }

    setFetchMoreLoading(true);

    try {
      abortController.current = new AbortController();
      const brandIds = bentoBrands.map((b) => b.id).join(",");
      const avoidIds = avoidBrands.map((b) => b.id).join(",");
      const url = `/api/organization/${currentOrg?.id}/bento-brands/inspired-by?ids=${brandIds}&avoid=${avoidIds}&size=${RESULTS_PER_PAGE}&cursor=${cursor}`;
      const res = await fetcherAuth(
        getToken,
        url,
        "GET",
        {},
        false,
        false,
        false,
        false,
        abortController.current.signal,
      );
      setSimilarBrands((prevBrands: BentoBrand[]) => [
        ...prevBrands,
        ...res.bentoBrands,
      ]);

      if (
        (res.cursor >= res.totalResults && res.totalResults !== 0) ||
        (res.cursor <= res.totalResults && res.totalResults < RESULTS_PER_PAGE)
      ) {
        trackEvent("Discover Page Reached End of Similar Brands List");
        setSimilarBrandsLimit(true);
      } else {
        setSimilarBrandsLimit(false);
      }
      setSimilarBrandsCursor(res.cursor);
    } catch (error) {
      if (error?.message?.includes("aborted")) {
        return;
      }
      setAlert(
        error?.message ||
          "Something went wrong when fetching brands, please reload and try again",
        "error",
      );
    } finally {
      setFetchMoreLoading(false);
    }
  };

  const callFetchInspiredBrands = (brands: BentoBrand[], cursor: number) => {
    const firstBrandName = brands[0]?.brandName;
    if (!firstBrandName) {
      return;
    }
    if (
      firstBrandName.toLowerCase() ===
      searchParams.get(SearchParams.QUERY_DISCOVER)?.toLowerCase()
    ) {
      fetchInspiredBrands([brands[0]], brands, cursor);
    } else {
      fetchInspiredBrands(brands.slice(0, 3), brands, cursor);
    }
  };

  const fetchBrands = async (
    searchQuery: string | null,
    fetchCursor: number[] | null,
    useFetchMoreLoading?: boolean,
    appendToList?: boolean,
  ) => {
    if (!currentOrg) {
      setBrandsLoading(false);
      return;
    }

    if (abortController?.current) {
      abortController.current?.abort();
    }

    let wasAborted = false;
    try {
      abortController.current = new AbortController();

      let query = searchQuery || "";
      if (selectedMetadataTags?.length > 0) {
        const clickedTags = selectedMetadataTags?.filter(
          (x) => x.type === MetadataType.tags,
        );
        query += clickedTags?.map((x) => x.value);
      }

      const encodedQuery = encodeURIComponent(query || "");

      let url = `/api/organization/${currentOrg?.id}/bento-brands?query=${encodedQuery}&size=${RESULTS_PER_PAGE}`;
      if (selectedSort) {
        url += `&sort=${selectedSort?.key}`;
      } else {
        url += `&sort=${Sort.Recommended}`;
      }

      if (selectedCategoryIds?.length > 0) {
        const categoryQueries = getCategoryIdParams(
          topLevelCategories,
          categoriesById,
          selectedCategoryIds,
        );
        url += `${arrayQuery(categoryQueries, "category_ids")}`;
      }

      if (selectedCompanyTypes?.length > 0) {
        url += `${arrayQuery(
          selectedCompanyTypes?.map((x) => x.key),
          "company_types",
        )}`;
      }

      if (selectedContactTypes?.length > 0) {
        url += `${arrayQuery(
          selectedContactTypes?.map((x) => x.key),
          "contact_types",
        )}`;
      }

      if (selectedCompensationTypes?.length > 0) {
        url += `${arrayQuery(
          selectedCompensationTypes?.map((x) => x.key),
          "compensation_types",
        )}`;
      }

      if (selectedLanguages?.length > 0) {
        url += `${arrayQuery(
          selectedLanguages?.map((x) => x.key),
          "languages",
        )}`;
      }

      if (selectedTargetGenders?.length > 0) {
        url += `${arrayQuery(
          selectedTargetGenders?.map((x) => x.key),
          "target_genders",
        )}`;
      }

      if (selectedLocations?.length > 0) {
        url += `${arrayQuery(
          selectedLocations?.map((x) => x.key),
          "locations",
        )}`;
      }

      if (selectedGeopoints?.length > 0) {
        url += `${arrayQuery(
          selectedGeopoints?.map((x) => x.key),
          "geopoints",
        )}`;
      }

      if (selectedInstagramFollowings?.length > 0) {
        const followers = getMinAndMaxInstagram(selectedInstagramFollowings);
        if (followers.min_instagram_followers) {
          url += `&min_instagram_followers=${followers.min_instagram_followers}`;
        }
        if (followers.max_instagram_followers) {
          url += `&max_instagram_followers=${followers.max_instagram_followers}`;
        }
      }
      if (fetchCursor) {
        url += `&cursor=${JSON.stringify(fetchCursor)}`;
      }

      if (useFetchMoreLoading) {
        setFetchMoreLoading(true);
      } else {
        setBrandsLoading(true);
      }
      const res = await fetcherAuth(
        getToken,
        url,
        "GET",
        {},
        {},
        false,
        false,
        true,
        abortController.current.signal,
      );

      if (res.bentoBrands.length < RESULTS_PER_PAGE) {
        setHitsLimit(true);
        if (!!res.totalResults && res.totalResults !== 0) {
          if (appendToList) {
            callFetchInspiredBrands([...brands, ...res.bentoBrands], 0);
          } else {
            callFetchInspiredBrands(res.bentoBrands, 0);
          }
        } else {
          setFetchMoreLoading(false);
          if (query) {
            fetchNoResultsBrands(query, null, false, false);
          }
        }
      } else {
        setHitsLimit(false);
        setFetchMoreLoading(false);
      }
      const matchQuery = query === lastSearchQuery;
      if (appendToList && matchQuery) {
        setBrands((prevBrands: BentoBrand[]) => {
          const brandSet = new Set(prevBrands.map((brand) => brand.id));
          const newBrands = [...prevBrands];

          res.bentoBrands.forEach((brand: BentoBrand) => {
            if (!brandSet.has(brand.id)) {
              brandSet.add(brand.id);
              if (newBrands) {
                newBrands.push(brand);
              }
            }
          });
          return newBrands;
        });
      } else {
        setBrands([...res.bentoBrands]);
      }
      setLastSearchQuery(res.query ? res.query : "");
      setCursor(res.cursor);
      setTotalCount(res.totalResults);
    } catch (error) {
      if (error?.message?.includes("aborted")) {
        wasAborted = true;
        return;
      }
      setAlert(
        error?.message ||
          "Something went wrong when fetching brands, please reload and try again",
        "error",
      );
    } finally {
      if (!wasAborted) {
        setBrandsLoading(false);
      }
    }
  };

  const fetchNoResultsBrands = async (
    searchQuery: string | null,
    fetchCursor: number[] | null,
    useFetchMoreLoading?: boolean,
    appendToList?: boolean,
  ) => {
    if (!currentOrg) {
      setNoFilterResultsLoading(false);
      return;
    }

    if (abortController?.current) {
      abortController.current?.abort();
    }

    try {
      abortController.current = new AbortController();

      let query = searchQuery || "";

      const encodedQuery = encodeURIComponent(query || "");

      let url = `/api/organization/${currentOrg?.id}/bento-brands?query=${encodedQuery}&size=${RESULTS_PER_PAGE}`;
      if (selectedSort) {
        url += `&sort=${selectedSort?.key}`;
      } else {
        url += `&sort=${Sort.Recommended}`;
      }

      if (fetchCursor) {
        url += `&cursor=${JSON.stringify(fetchCursor)}`;
      }

      if (useFetchMoreLoading) {
        setNoFilterResultsFetchMoreLoading(true);
      } else {
        setNoFilterResultsLoading(true);
      }
      const res = await fetcherAuth(
        getToken,
        url,
        "GET",
        {},
        {},
        false,
        false,
        true,
        abortController.current.signal,
      );

      if (res.bentoBrands.length < RESULTS_PER_PAGE) {
        setNoFilterResultsLimit(true);
      } else {
        setNoFilterResultsLimit(false);
      }
      if (appendToList) {
        setNoFilterResultBrands((prevBrands: BentoBrand[]) => {
          const brandSet = new Set(prevBrands.map((brand) => brand.id));
          const newBrands = [...prevBrands];

          res.bentoBrands.forEach((brand: BentoBrand) => {
            if (!brandSet.has(brand.id)) {
              brandSet.add(brand.id);
              if (newBrands) {
                newBrands.push(brand);
              }
            }
          });
          return newBrands;
        });
      } else {
        setNoFilterResultBrands([...res.bentoBrands]);
      }
      setNoFilterResultsCursor(res.cursor);
    } catch (error) {
      if (error?.message?.includes("aborted")) {
        return;
      }
      setAlert(
        error?.message ||
          "Something went wrong when fetching brands, please reload and try again",
        "error",
      );
    } finally {
      if (useFetchMoreLoading) {
        setNoFilterResultsFetchMoreLoading(false);
      } else {
        setNoFilterResultsLoading(false);
      }
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-search-filters`,
        "GET",
      );
      setSearchFilters(res.bentoSearchFilters);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleSearch = async () => {
    scrollToTop();
    setSimilarBrands([]);
    setNoFilterResultBrands([]);
    setNoFilterResultsCursor(null);
    setBrandsLoading(true);
    setSimilarBrandsCursor(0);
    setCursor(null);
    setSimilarBrandsLimit(false);
    setHitsLimit(false);
    setTotalCount(null);
    fetchBrands(brandQuery, null, false, false);
  };

  const scrollToTop = () => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const handleDialogLogic = async () => {
    if (
      !profileDialogOpen &&
      incompleteProfile &&
      !upgradeDialogSource &&
      !profileDialogWasOpened
    ) {
      trackEvent("Onboarding Action Items Dialog Opened", {
        route: location.pathname,
      });
      setProfileDialogOpen(true);
      setProfileDialogWasOpened(true);
      setHideCityDialog(true);
      return;
    }

    const alreadyDisplayedCityDialog =
      localStorage.getItem(`${DISPLAYED_CITY_DIALOG}-${currentOrg?.id}`) ===
      "true";
    if (
      profile &&
      !profile?.city &&
      !alreadyDisplayedCityDialog &&
      !hideCityDialog
    ) {
      setOpenCityDialog(true);
    }
  };

  useEffect(() => {
    if (isBrandsRefVisible) {
      if (!hitsLimit && !brandsLoading) {
        fetchBrands(brandQuery, cursor, true, true);
        trackEvent("Discover Page Scrolled To Bottom", {
          keyword: brandQuery,
          categoryIds: selectedCategoryIds,
          "Company Type": selectedCompanyTypes,
          "Contact Type": selectedContactTypes?.map((x) => x.key),
          sort: selectedSort,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrandsRefVisible, brandsLoading]);

  useEffect(() => {
    if (isNoFilterResultsRefVisible) {
      if (
        !noFilterResultsLimit &&
        !noFilterResultsLoading &&
        !fetchMoreLoading &&
        !brandsLoading &&
        !noFilterResultsFetchMoreLoading
      ) {
        fetchNoResultsBrands(brandQuery, noFilterResultsCursor, true, true);
        trackEvent(
          "Discover Page Scrolled To Bottom of No Filter Brand Results",
          {
            keyword: brandQuery,
            sort: selectedSort,
          },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNoFilterResultsRefVisible]);

  useEffect(() => {
    if (isSimilarBrandsRefVisible) {
      if (!similarBrandsLimit && !fetchMoreLoading && !brandsLoading) {
        callFetchInspiredBrands(brands, similarBrandsCursor);
        trackEvent("Discover Page Scrolled To Bottom of Similar Brands List", {
          keyword: brandQuery,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSimilarBrandsRefVisible]);

  useEffect(() => {
    const isElementInView = () => {
      const rect = headRef?.current?.getBoundingClientRect();
      if (!rect) {
        return false;
      }
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    };

    if (headRef.current && !isElementInView()) {
      headRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
    if (query && !window.location.href?.includes("?")) {
      navigate(`?${query}`);
    } else {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(discoverFilterParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    selectedMetadataTags?.length,
    brandQuery,
    currentOrg?.id,
  ]);

  useEffect(
    () => () => {
      if (abortController.current) {
        abortController.current?.abort();
      }
      if (query) {
        setBrands([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    if (currentOrg?.id && searchFilters?.length === 0) {
      fetchFilters();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    handleDialogLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.city, currentOrg?.id, incompleteProfile]);

  useEffect(() => {
    if (!profileDialogOpen && profileDialogWasOpened) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDialogOpen]);

  const handleCloseCityDialog = (city?: string) => {
    localStorage.setItem(`${DISPLAYED_CITY_DIALOG}-${currentOrg?.id}`, "true");
    setOpenCityDialog(false);
    if (city) {
      searchParams.set(SearchParams.LOCATION_DISCOVER, city);
      setSearchParams(searchParams);
    }
  };

  return (
    <APIProvider apiKey={API_KEY || ""}>
      <div ref={resultsRef} />
      <Box>
        {!categoriesLoading && (
          <DiscoverFilter
            totalCount={totalCount}
            brandsLoading={brandsLoading || categoriesLoading}
          />
        )}

        <Grid
          container
          item
          xs={12}
          columnSpacing={1}
          sx={
            breaksDiscoverScreen
              ? styles.cardContainerMobile
              : styles.cardContainer
          }
        >
          {!breaksDiscoverScreen && !categoriesLoading && (
            <Grid item md={1.5}>
              <CategoryFilter topLevelCategories={topLevelCategories} />
            </Grid>
          )}

          <Grid
            md={!breaksDiscoverScreen && !categoriesLoading ? 10.5 : true}
            container
            alignItems="flex-start"
            direction="column"
            item
            sx={styles.brandsList}
          >
            <BrandsList
              brands={brands}
              brandsLoading={brandsLoading || categoriesLoading}
              fetchMoreLoading={fetchMoreLoading}
              totalCount={totalCount}
              ref={ref}
            />
            <SimilarBrands
              brandsLoading={brandsLoading || categoriesLoading}
              fetchMoreLoading={fetchMoreLoading}
              ref={similarBrandsRef}
            />
            {brandQuery &&
              brandQuery?.length > 0 &&
              brands.length === 0 &&
              !brandsLoading &&
              !fetchMoreLoading && (
                <NoFilterResultBrands
                  brandsLoading={noFilterResultsLoading || categoriesLoading}
                  fetchMoreLoading={noFilterResultsFetchMoreLoading}
                  ref={noFilterResultsRef}
                />
              )}
          </Grid>
        </Grid>

        <CityDialog open={openCityDialog} handleClose={handleCloseCityDialog} />

        <Routes>
          <Route path="send/:bentoBrandId/*" element={<OpenSendDialog />} />
        </Routes>
      </Box>
      <PopoverSavedCollection />
    </APIProvider>
  );
}
